// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    Elements,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button, CircularProgress } from '@mui/material';

import { ReactComponent as CardsRow } from '@assets/custom-icons/card-brands-row.svg';
import { t } from '@i18n';

import './styles.scss';

// The split Card Elements render inside Stripe iframes, so page CSS cannot
// reach the text — colors and typography must be passed in as options.
// Stripe's style validator only accepts simple color syntaxes (hex, rgb(),
// legacy comma-separated hsl()); a token like `hsl(354.83 100% 70.39%)`
// throws an IntegrationError that aborts the element mount and leaves the
// fields dead, so every token is resolved to rgb() through a DOM probe
// before it reaches Stripe.
function readCssToken(name, fallback) {
    if (typeof window === 'undefined') {
        return fallback;
    }

    const raw = getComputedStyle(document.body).getPropertyValue(name).trim();

    if (!raw) {
        return fallback;
    }

    const probe = document.createElement('span');
    probe.style.color = raw;

    if (!probe.style.color) {
        return fallback;
    }

    document.body.appendChild(probe);
    const resolved = getComputedStyle(probe).color;
    probe.remove();

    return resolved || fallback;
}

export function buildCardElementStyle() {
    const textColor = readCssToken('--surface-2', '#111212');
    const placeholderColor = readCssToken('--surface-5', '#7e8183');
    const invalidColor = readCssToken('--error', '#ff6876');

    return {
        base: {
            color: textColor,
            fontFamily: '"League Spartan", sans-serif',
            fontSize: '16px',
            fontWeight: '300',
            '::placeholder': {
                color: placeholderColor,
            },
        },
        invalid: {
            color: invalidColor,
        },
    };
}

export const CARD_ELEMENT_STYLE = {
    base: {
        color: '#111212',
        fontFamily: '"League Spartan", sans-serif',
        fontSize: '16px',
        fontWeight: '300',
        '::placeholder': {
            color: '#7e8183',
        },
    },
    invalid: {
        color: '#ff6876',
    },
};

export function buildElementsOptions(locale = 'en-US') {
    return {
    locale,
    fonts: [
        { cssSrc: 'https://fonts.googleapis.com/css2?family=League+Spartan:wght@300;400&display=swap' },
    ],
    };
}

export const ELEMENTS_OPTIONS = buildElementsOptions('pt-BR');

// One entry per element, keyed by Stripe's elementType. A field is
// confirmable only when Stripe reported it complete and error-free.
export function buildInitialFieldState() {
    return {
        cardNumber: { complete: false, error: null },
        cardExpiry: { complete: false, error: null },
        cardCvc: { complete: false, error: null },
    };
}

// Returns the message blocking submission, or null when the card is ready.
export function resolveCardFieldsError(fieldState) {
    const fields = Object.values(fieldState);

    const withError = fields.find((field) => field.error);

    if (withError) {
        return withError.error;
    }

    if (fields.some((field) => !field.complete)) {
        return t('stripe.error.incompleteCard');
    }

    return null;
}

export function buildCardPaymentData(card, billingDetails) {
    const paymentMethod = { card };

    if (billingDetails && Object.keys(billingDetails).length > 0) {
        paymentMethod.billing_details = billingDetails;
    }

    return { payment_method: paymentMethod };
}

export default function StripePayment({
    publishableKey,
    stripeAccount,
    locale,
    preValidate,
    createIntent,
    getBillingDetails,
    onConfirmed,
    onError,
    beforeSubmit,
    submitDisabled = false,
}) {
    const stripePromise = useMemo(() => {
        if (!publishableKey) {
            return null;
        }

        // Direct charge: the PaymentIntent lives on the creator's connected
        // account — Stripe.js must be scoped to it or the client secret
        // resolves nothing.
        if (stripeAccount) {
            return loadStripe(publishableKey, { stripeAccount });
        }

        return loadStripe(publishableKey);
    }, [publishableKey, stripeAccount]);
    const elementsOptions = useMemo(() => buildElementsOptions(locale), [locale]);

    if (!publishableKey) {
        return null;
    }

    return (
        <div className="stripe-payment-box">
            <Elements stripe={stripePromise} options={elementsOptions}>
                <StripePaymentForm
                    preValidate={preValidate}
                    createIntent={createIntent}
                    getBillingDetails={getBillingDetails}
                    onConfirmed={onConfirmed}
                    onError={onError}
                    beforeSubmit={beforeSubmit}
                    submitDisabled={submitDisabled}
                />
            </Elements>
        </div>
    );
}

export function StripePaymentForm({ preValidate, createIntent, getBillingDetails, onConfirmed, onError, beforeSubmit, submitDisabled = false }) {
    const stripe = useStripe();
    const elements = useElements();
    const [cardElementStyle, setCardElementStyle] = useState(buildCardElementStyle);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fieldState, setFieldState] = useState(buildInitialFieldState);

    // The theme toggle swaps the CSS vars on <body>, but the mounted iframes
    // keep the colors they were created with — rebuild on the app's
    // appearance event so react-stripe-js pushes the new palette in.
    useEffect(() => {
        const applyTheme = () => setCardElementStyle(buildCardElementStyle());

        window.addEventListener('appearence-change', applyTheme);

        return () => window.removeEventListener('appearence-change', applyTheme);
    }, []);

    function handleFieldChange(event) {
        setFieldState((previous) => ({
            ...previous,
            [event.elementType]: {
                complete: !!event.complete,
                error: event.error?.message || null,
            },
        }));
    }

    function fieldClassName(base, elementType) {
        return fieldState[elementType]?.error ? `${base} stripe-field-invalid` : base;
    }

    async function handleSubmit(event) {
        event.preventDefault();

        // The button is disabled while payments are blocked, but the form can
        // still be submitted by pressing Enter inside a card field.
        if (!stripe || !elements || submitDisabled) {
            return;
        }

        setLoading(true);
        setError(null);

        const fail = (message) => {
            setError(message);
            onError?.(message);
            setLoading(false);
        };

        try {
            preValidate?.();

            // The intent is only created on submit, so card completeness has
            // to be checked here from the elements' change events.
            const cardError = resolveCardFieldsError(fieldState);

            if (cardError) {
                fail(cardError);
                return;
            }

            const intent = await createIntent();

            if (!intent?.clientSecret) {
                fail(t('stripe.error.generic'));
                return;
            }

            // 3DS opens in Stripe's modal — the buyer never leaves the page.
            const result = await stripe.confirmCardPayment(
                intent.clientSecret,
                buildCardPaymentData(elements.getElement(CardNumberElement), getBillingDetails?.() || null)
            );

            if (result?.error) {
                fail(result.error.message || t('stripe.error.confirmPayment'));
                return;
            }

            // Keep the button busy while the page polls the charge; a rejected
            // poll (declined/canceled) resurfaces the form for another try.
            await onConfirmed?.(result?.paymentIntent || null);
        } catch (submitError) {
            fail(submitError?.message || t('stripe.error.generic'));
        }
    }

    return (
        <form className="stripe-payment-form" data-testid="stripe-payment-form" onSubmit={handleSubmit}>
            <div className="input-group">
                <div className={fieldClassName('stripe-field stripe-field-number', 'cardNumber')}>
                    <div className="stripe-field-element">
                        <CardNumberElement
                            options={{
                                style: cardElementStyle,
                                placeholder: t('input.cardNumber.placeholder'),
                                showIcon: false,
                            }}
                            onChange={handleFieldChange}
                        />
                    </div>
                    <CardsRow className="stripe-card-brands" />
                </div>

                <div className="stripe-field-row">
                    <div className={fieldClassName('stripe-field stripe-field-expiry', 'cardExpiry')}>
                        <div className="stripe-field-element">
                            <CardExpiryElement
                                options={{ style: cardElementStyle, placeholder: t('checkout.card.expiryPlaceholder') }}
                                onChange={handleFieldChange}
                            />
                        </div>
                    </div>
                    <div className={fieldClassName('stripe-field stripe-field-cvc', 'cardCvc')}>
                        <div className="stripe-field-element">
                            <CardCvcElement
                                options={{ style: cardElementStyle, placeholder: t('checkout.card.cvv') }}
                                onChange={handleFieldChange}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {error && <span className="stripe-payment-error">{error}</span>}

            {beforeSubmit}

            <Button
                className={`submit-button${loading ? ' submit-button--loading' : ''}`}
                type="submit"
                disabled={!stripe || loading || submitDisabled}
            >
                {loading ? <CircularProgress size={20} color="inherit" /> : t('stripe.submit')}
            </Button>
        </form>
    );
}
